import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import { logger } from '@/lib/logger';
import { CategoryLogo } from './CategoryLogo';
import { heroTranslations } from './translations';
import type { HeroTile } from './useMarketplaceHero';

interface HeroTileRowProps {
  tiles: HeroTile[];
}

/**
 * The Hero's app strip, laid out like playerok's: a full-bleed horizontal rail
 * of square app icons with the name centred underneath, no card frame. Each
 * tile links to its category page.
 *
 * Rendered from `GET /marketplace/hero`'s `tiles` (`CatalogApp.heroFeatured` /
 * `heroOrder`), the same source the admin preview reads.
 */
export function HeroTileRow({ tiles }: HeroTileRowProps) {
  const t = useSectionTranslation(heroTranslations);
  const { direction } = useLocale();
  const railRef = useRef<HTMLDivElement>(null);
  const isRTL = direction === 'rtl';

  if (tiles.length === 0) return null;

  const scrollBy = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => scrollBy(isRTL ? 1 : -1)}
        aria-label={t.tiles.scrollBack}
        className="absolute start-0 top-[40px] z-10 hidden -translate-y-1/2 md:flex h-9 w-9 items-center justify-center rounded-full border border-surface-border bg-surface-card/90 backdrop-blur text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
      >
        <ChevronLeft size={16} style={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />
      </button>

      <div
        ref={railRef}
        className="flex gap-[9px] overflow-x-auto no-scrollbar scroll-smooth px-1 pt-2"
      >
        {tiles.map((tile) => (
          <Link
            key={tile.id}
            to={`/category/${tile.slug}`}
            onClick={() => logger.info('Hero tile clicked', { slug: tile.slug })}
            className="group relative flex w-[80px] shrink-0 flex-col items-center"
          >
            <span className="relative size-20 shrink-0 overflow-hidden rounded-2xl border border-surface-border bg-surface-card transition-colors group-hover:border-accent/30">
              <CategoryLogo slug={tile.slug} name={tile.name} src={tile.logo} padding="p-2" />
            </span>
            <span className="mt-1.5 line-clamp-2 w-full text-center text-sm font-normal text-brand-muted transition-colors group-hover:text-brand-white">
              {tile.name}
            </span>
            {tile.isNew && (
              <span className="absolute -top-1 -end-1 rounded-full bg-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-surface-base">
                {t.tiles.new}
              </span>
            )}
          </Link>
        ))}
      </div>

      <button
        type="button"
        onClick={() => scrollBy(isRTL ? -1 : 1)}
        aria-label={t.tiles.scrollMore}
        className="absolute end-0 top-[40px] z-10 hidden -translate-y-1/2 md:flex h-9 w-9 items-center justify-center rounded-full border border-surface-border bg-surface-card/90 backdrop-blur text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
      >
        <ChevronRight size={16} style={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />
      </button>
    </div>
  );
}
